import {BaseTrackingEvent} from '@udemy-v2/event-tracking';

interface TrackableBuyable {
    id: number;
    type: 'course' | 'subscription' | 'learning_path';
    trackingId: string;
}

export enum IncentivesPlacement {
    BODY = 'body',
    SIDEBAR = 'sidebar',
}

export enum LearningProduct {
    COURSE = 'course',
    LEARNING_PATH = 'learning_path',
}

/**
 * CLPViewEvent is fired when a CLP Page is loaded on frontend
 */
export class CLPViewEvent implements BaseTrackingEvent {
    _type = 'CLPViewEvent';
    courseId: number;
    courseTrackingId: string;
    isDirectLanded: boolean;
    isPurchased?: boolean;

    constructor({
        courseId,
        courseTrackingId,
        isDirectLanded,
        isPurchased,
    }: {
        courseId: number;
        courseTrackingId: string;
        isDirectLanded: boolean;
        isPurchased?: boolean;
    }) {
        this.courseId = courseId;
        this.courseTrackingId = courseTrackingId;
        this.isDirectLanded = isDirectLanded;
        this.isPurchased = isPurchased;
    }
}

export class BuyNowEvent implements BaseTrackingEvent {
    _type = 'BuyNowEvent';
    buyable: TrackableBuyable;

    constructor({buyable}: {buyable: TrackableBuyable}) {
        this.buyable = buyable;
    }
}

export class EnrollNowEvent implements BaseTrackingEvent {
    _type = 'EnrollNowEvent';
    buyable: TrackableBuyable;

    constructor({buyable}: {buyable: TrackableBuyable}) {
        this.buyable = buyable;
    }
}

interface InstructorResponse {
    responseId: number;
    instructorTrackingId: string;
}

/**
 * Fired when Incentives component is viewed by user
 */
export class CourseIncentivesImpressionEvent implements BaseTrackingEvent {
    _type = 'CourseIncentivesImpressionEvent';
    courseTrackingId?: string | null;
    uiRegion?: string | null;
    learningProduct?: {
        type: LearningProduct;
        id: number;
        trackingId: string;
    } | null;

    constructor({
        courseTrackingId = null,
        uiRegion = null,
        learningProduct = null,
    }: {
        courseTrackingId?: string | null;
        uiRegion?: IncentivesPlacement | null;
        learningProduct?: {
            type: LearningProduct;
            id: number;
            trackingId: string;
        } | null;
    } = {}) {
        this.courseTrackingId = courseTrackingId;
        this.uiRegion = uiRegion;
        this.learningProduct = learningProduct;
    }
}

/**
 * Fired when a course incentive comes into view
 */
export class CourseIncentiveImpressionEvent implements BaseTrackingEvent {
    _type = 'CourseIncentiveImpressionEvent';
    courseId: number;
    courseTrackingId: string;
    incentiveType: IncentiveType;
    incentiveValue: string;
    position: number;

    constructor({
        courseId,
        courseTrackingId,
        incentiveType,
        incentiveValue,
        position,
    }: {
        courseId: number;
        courseTrackingId: string;
        incentiveType: IncentiveType;
        incentiveValue: string;
        position: number;
    }) {
        this.courseId = courseId;
        this.courseTrackingId = courseTrackingId;
        this.incentiveType = incentiveType;
        this.incentiveValue = incentiveValue;
        this.position = position;
    }
}

export const enum IncentiveType {
    VIDEO_CONTENT_LENGTH = 'video_content_length',
    AUDIO_CONTENT_LENGTH = 'audio_content_length',
    NUM_ARTICLES = 'num_articles',
    NUM_RESOURCES = 'num_resources',
    NUM_QUIZZES = 'num_quizzes',
    NUM_PRACTICE_TESTS = 'num_practice_tests',
    NUM_CODING_EXERCISES = 'num_coding_exercises',
    HAS_LIFETIME_ACCESS = 'has_lifetime_access',
    DEVICES_ACCESS = 'devices_access',
    SUBSCRIPTION_DIFFERENTIATION = 'subscription_differentiation',
    HAS_ASSIGNMENTS = 'has_assignments',
    HAS_CERTIFICATE = 'has_certificate',
    NUM_CPE_CREDITS = 'num_cpe_credits',
    HAS_CLOSED_CAPTIONS = 'has_closed_captions',
    HAS_AUDIO_DESCRIPTION = 'has_audio_description',
    ROLE_PLAY = 'role_play',
}

/**
 * Fired when ShortcutNavigation section is viewed by user
 */
export class ShortcutNavigationImpressionEvent implements BaseTrackingEvent {
    _type = 'ShortcutNavigationImpressionEvent';
    courseTrackingId: string;

    constructor({courseTrackingId}: {courseTrackingId: string}) {
        this.courseTrackingId = courseTrackingId;
    }
}

/**
 * Fired when ShortcutNavigation section is clicked by user
 */
export class ShortcutNavigationClickEvent implements BaseTrackingEvent {
    _type = 'ShortcutNavigationClickEvent';
    courseTrackingId: string;
    sectionTitle: string;

    constructor({
        courseTrackingId,
        sectionTitle,
    }: {
        courseTrackingId: string;
        sectionTitle: string;
    }) {
        this.courseTrackingId = courseTrackingId;
        this.sectionTitle = sectionTitle;
    }
}

export class ReviewImpressionEvent implements BaseTrackingEvent {
    _type = 'ReviewImpressionEvent';
    courseTrackingId: string;
    reviewId: number;
    isFeatured: boolean;
    instructorResponses: InstructorResponse[];
    reviewableObjectId: number;
    reviewableObjectType: string;

    constructor({
        courseTrackingId,
        reviewId,
        isFeatured,
        instructorResponses,
        reviewableObjectId,
        reviewableObjectType,
    }: {
        courseTrackingId: string;
        reviewId: number;
        isFeatured: boolean;
        instructorResponses: InstructorResponse[];
        reviewableObjectId: number;
        reviewableObjectType: string;
    }) {
        this.courseTrackingId = courseTrackingId;
        this.reviewId = reviewId;
        this.isFeatured = isFeatured;
        this.instructorResponses = instructorResponses;
        this.reviewableObjectId = reviewableObjectId;
        this.reviewableObjectType = reviewableObjectType;
    }
}

/**
 * Fired when Reviews section is seen by user
 */
export class ReviewsImpressionEvent implements BaseTrackingEvent {
    _type = 'ReviewsImpressionEvent';
    courseTrackingId: string;
    reviewableObjectId: number;
    reviewableObjectType: string;

    constructor({
        courseTrackingId,
        reviewableObjectId,
        reviewableObjectType,
    }: {
        courseTrackingId: string;
        reviewableObjectId: number;
        reviewableObjectType: string;
    }) {
        this.courseTrackingId = courseTrackingId;
        this.reviewableObjectId = reviewableObjectId;
        this.reviewableObjectType = reviewableObjectType;
    }
}

/**
 * Fired when Ratings Summary is seen by user
 */
export class RatingsSummaryImpressionEvent implements BaseTrackingEvent {
    _type = 'RatingsSummaryImpressionEvent';
    courseTrackingId: string;
    reviewableObjectId: number;
    reviewableObjectType: string;

    constructor({
        courseTrackingId,
        reviewableObjectId,
        reviewableObjectType,
    }: {
        courseTrackingId: string;
        reviewableObjectId: number;
        reviewableObjectType: string;
    }) {
        this.courseTrackingId = courseTrackingId;
        this.reviewableObjectId = reviewableObjectId;
        this.reviewableObjectType = reviewableObjectType;
    }
}

export class ReviewFeedbackSubmitEvent implements BaseTrackingEvent {
    _type = 'ReviewFeedbackSubmitEvent';
    courseTrackingId: string;
    reviewId: number;
    isFeatured: boolean;
    isHelpful: boolean;
    reviewableObjectId: number;
    reviewableObjectType: string;

    constructor({
        courseTrackingId,
        reviewId,
        isFeatured,
        isHelpful,
        reviewableObjectId,
        reviewableObjectType,
    }: {
        courseTrackingId: string;
        reviewId: number;
        isFeatured: boolean;
        isHelpful: boolean;
        reviewableObjectId: number;
        reviewableObjectType: string;
    }) {
        this.courseTrackingId = courseTrackingId;
        this.reviewId = reviewId;
        this.isFeatured = isFeatured;
        this.isHelpful = isHelpful;
        this.reviewableObjectId = reviewableObjectId;
        this.reviewableObjectType = reviewableObjectType;
    }
}

export class ReviewFeedbackClearEvent implements BaseTrackingEvent {
    _type = 'ReviewFeedbackClearEvent';
    courseTrackingId: string;
    reviewId: number;
    isFeatured: boolean;
    wasHelpful: boolean;
    reviewableObjectId: number;
    reviewableObjectType: string;

    constructor({
        courseTrackingId,
        reviewId,
        isFeatured,
        wasHelpful,
        reviewableObjectId,
        reviewableObjectType,
    }: {
        courseTrackingId: string;
        reviewId: number;
        isFeatured: boolean;
        wasHelpful: boolean;
        reviewableObjectId: number;
        reviewableObjectType: string;
    }) {
        this.courseTrackingId = courseTrackingId;
        this.reviewId = reviewId;
        this.isFeatured = isFeatured;
        this.wasHelpful = wasHelpful;
        this.reviewableObjectId = reviewableObjectId;
        this.reviewableObjectType = reviewableObjectType;
    }
}

export class ReviewReportEvent implements BaseTrackingEvent {
    _type = 'ReviewReportEvent';
    courseTrackingId: string;
    reviewId: number;
    isFeatured: boolean;
    reviewableObjectId: number;
    reviewableObjectType: string;

    constructor({
        courseTrackingId,
        reviewId,
        isFeatured,
        reviewableObjectId,
        reviewableObjectType,
    }: {
        courseTrackingId: string;
        reviewId: number;
        isFeatured: boolean;
        reviewableObjectId: number;
        reviewableObjectType: string;
    }) {
        this.courseTrackingId = courseTrackingId;
        this.reviewId = reviewId;
        this.isFeatured = isFeatured;
        this.reviewableObjectId = reviewableObjectId;
        this.reviewableObjectType = reviewableObjectType;
    }
}

export class ReviewExpandEvent implements BaseTrackingEvent {
    _type = 'ReviewExpandEvent';
    courseTrackingId: string;
    reviewId: number;
    isFeatured: boolean;
    reviewableObjectId: number;
    reviewableObjectType: string;

    constructor({
        courseTrackingId,
        reviewId,
        isFeatured,
        reviewableObjectId,
        reviewableObjectType,
    }: {
        courseTrackingId: string;
        reviewId: number;
        isFeatured: boolean;
        reviewableObjectId: number;
        reviewableObjectType: string;
    }) {
        this.courseTrackingId = courseTrackingId;
        this.reviewId = reviewId;
        this.isFeatured = isFeatured;
        this.reviewableObjectId = reviewableObjectId;
        this.reviewableObjectType = reviewableObjectType;
    }
}

/**
 * Fired when 'See More Reviews' is clicked by the user.
 */
export class SeeMoreReviewsClickEvent implements BaseTrackingEvent {
    _type = 'SeeMoreReviewsClickEvent';
    courseTrackingId: string;
    reviewableObjectId: number;
    reviewableObjectType: string;

    constructor({
        courseTrackingId,
        reviewableObjectId,
        reviewableObjectType,
    }: {
        courseTrackingId: string;
        reviewableObjectId: number;
        reviewableObjectType: string;
    }) {
        this.courseTrackingId = courseTrackingId;
        this.reviewableObjectId = reviewableObjectId;
        this.reviewableObjectType = reviewableObjectType;
    }
}

/**
 * Fired when more reviews are loaded onto the CLP.
 */
export class MoreReviewsLoadEvent implements BaseTrackingEvent {
    _type = 'MoreReviewsLoadEvent';
    courseTrackingId: string;
    numReviewsLoaded: number;
    reviewableObjectId: number;
    reviewableObjectType: string;

    constructor({
        courseTrackingId,
        numReviewsLoaded,
        reviewableObjectId,
        reviewableObjectType,
    }: {
        courseTrackingId: string;
        numReviewsLoaded: number;
        reviewableObjectId: number;
        reviewableObjectType: string;
    }) {
        this.courseTrackingId = courseTrackingId;
        this.numReviewsLoaded = numReviewsLoaded;
        this.reviewableObjectId = reviewableObjectId;
        this.reviewableObjectType = reviewableObjectType;
    }
}

/**
 * Fired when reviews search query is submitted.
 */
export class ReviewsSearchQuerySubmitEvent implements BaseTrackingEvent {
    _type = 'ReviewsSearchQuerySubmitEvent';
    courseTrackingId: string;
    query: string | null;
    reviewableObjectId: number;
    reviewableObjectType: string;

    constructor({
        courseTrackingId,
        query,
        reviewableObjectId,
        reviewableObjectType,
    }: {
        courseTrackingId: string;
        query: string | null;
        reviewableObjectId: number;
        reviewableObjectType: string;
    }) {
        this.courseTrackingId = courseTrackingId;
        this.query = query;
        this.reviewableObjectId = reviewableObjectId;
        this.reviewableObjectType = reviewableObjectType;
    }
}

/**
 * Fired when reviews are filtered by star rating.
 */
export class ReviewsFilterSubmitEvent implements BaseTrackingEvent {
    _type = 'ReviewsFilterSubmitEvent';
    courseTrackingId: string;
    rating: number | null;
    query: string | null;
    reviewableObjectId: number;
    reviewableObjectType: string;

    constructor({
        courseTrackingId,
        rating,
        query,
        reviewableObjectId,
        reviewableObjectType,
    }: {
        courseTrackingId: string;
        rating: number | null;
        query: string | null;
        reviewableObjectId: number;
        reviewableObjectType: string;
    }) {
        this.courseTrackingId = courseTrackingId;
        this.rating = rating;
        this.query = query;
        this.reviewableObjectId = reviewableObjectId;
        this.reviewableObjectType = reviewableObjectType;
    }
}

/**
 * Fired when a user shares a course (general share event, not channel-specific)
 */
export class CourseShareEvent implements BaseTrackingEvent {
    readonly _type = 'CourseShareEvent';
    courseId: number;

    constructor({courseId}: {courseId: number}) {
        this.courseId = courseId;
    }
}

/**
 * Fired when a user shares a course through a social network or other channel
 */
export class CourseShareToChannelEvent implements BaseTrackingEvent {
    readonly _type = 'CourseShareToChannelEvent';
    courseId: number;
    channel: string;

    constructor({courseId, channel}: {courseId: number; channel: string}) {
        this.courseId = courseId;
        this.channel = channel;
    }
}

/**
 * Fired when Curriculum component is viewed by user
 */
export class CurriculumImpressionEvent implements BaseTrackingEvent {
    _type = 'CurriculumImpressionEvent';
    learningProduct: {
        type: LearningProduct;
        id: number;
        trackingId: string;
    };

    constructor({type, id, trackingId}: {type: LearningProduct; id: number; trackingId: string}) {
        this.learningProduct = {
            type,
            id,
            trackingId,
        };
    }
}

export const enum PracticeIncentivesType {
    HAS_ASSIGNMENTS = 'has_assignments',
    NUM_CODING_EXERCISES = 'num_coding_exercises',
    NUM_PRACTICE_TESTS = 'num_practice_tests',
    NUM_QUIZZES = 'num_quizzes',
}

export class PracticeIncentiveImpressionEvent implements BaseTrackingEvent {
    _type = 'PracticeIncentiveImpressionEvent';
    learningProduct: LearningProduct | null;
    incentiveType: PracticeIncentivesType;
    position: number;

    constructor({
        learningProduct = null,
        incentiveType,
        position,
    }: {
        learningProduct?: LearningProduct | null;
        incentiveType: PracticeIncentivesType;
        position: number;
    }) {
        this.learningProduct = learningProduct;
        this.incentiveType = incentiveType;
        this.position = position;
    }
}

export class PracticeIncentivesImpressionEvent implements BaseTrackingEvent {
    _type = 'PracticeIncentivesImpressionEvent';
    learningProduct: LearningProduct | null;

    constructor({
        learningProduct = null,
    }: {
        learningProduct?: LearningProduct | null;
    } = {}) {
        this.learningProduct = learningProduct;
    }
}

export class PracticeIncentivesCarouselActionEvent implements BaseTrackingEvent {
    _type = 'PracticeIncentivesCarouselActionEvent';
    learningProduct: LearningProduct | null;
    action: 'scroll' | 'button-click' | null;

    constructor({
        learningProduct = null,
        action = null,
    }: {
        learningProduct?: LearningProduct | null;
        action?: 'scroll' | 'button-click' | null;
    } = {}) {
        this.learningProduct = learningProduct;
        this.action = action;
    }
}

export class PracticeIncentiveShowMoreClickEvent implements BaseTrackingEvent {
    _type = 'PracticeIncentiveShowMoreClickEvent';
    learningProduct: LearningProduct | null;
    position: number | null;

    constructor({
        learningProduct = null,
        position = null,
    }: {
        learningProduct?: LearningProduct | null;
        position?: number | null;
    } = {}) {
        this.learningProduct = learningProduct;
        this.position = position;
    }
}

/**
 * Fired when the reviews carousel is scrolled
 */
export class ReviewsCarouselScrollEvent implements BaseTrackingEvent {
    _type = 'ReviewsCarouselScrollEvent';
    courseTrackingId: string;
    reviewableObjectId: number;
    reviewableObjectType: string;

    constructor({
        courseTrackingId,
        reviewableObjectId,
        reviewableObjectType,
    }: {
        courseTrackingId: string;
        reviewableObjectId: number;
        reviewableObjectType: string;
    }) {
        this.courseTrackingId = courseTrackingId;
        this.reviewableObjectId = reviewableObjectId;
        this.reviewableObjectType = reviewableObjectType;
    }
}

export class ClickEvent implements BaseTrackingEvent {
    _type = 'ClickEvent';
    componentName: string;
    trackingId: string;
    relatedObjectType: string;
    relatedObjectId: number;

    constructor({
        componentName,
        trackingId,
        relatedObjectType,
        relatedObjectId,
    }: {
        componentName: string;
        trackingId: string;
        relatedObjectType: string;
        relatedObjectId: number;
    }) {
        this.componentName = componentName;
        this.trackingId = trackingId;
        this.relatedObjectType = relatedObjectType;
        this.relatedObjectId = relatedObjectId;
    }
}

/**
 * Fires when gift flow starts
 */
export class GiftBuyablesStartEvent implements BaseTrackingEvent {
    _type = 'GiftBuyablesStartEvent';
    buyables: TrackableBuyable[];
    uiRegion: string;

    constructor(buyables: TrackableBuyable[], uiRegion: string) {
        this.buyables = buyables;
        this.uiRegion = uiRegion;
    }

    get allBuyables() {
        return this.buyables;
    }
}

/**
 * Fired when a preview video starts playing
 */
export class PreviewVideoPlayEvent implements BaseTrackingEvent {
    _type = 'PreviewVideoPlayEvent';
    courseId: number;
    buyableTrackingId?: string;
    previewId: number;
    offset: number;
    nonInteraction: boolean;
    uiRegion?: string;

    constructor({
        courseId,
        buyableTrackingId,
        previewId,
        offset = 0,
        nonInteraction = false,
        uiRegion,
    }: {
        courseId: number;
        buyableTrackingId?: string;
        previewId: number;
        offset?: number;
        nonInteraction?: boolean;
        uiRegion?: string;
    }) {
        this.courseId = courseId;
        this.buyableTrackingId = buyableTrackingId;
        this.previewId = previewId;
        this.offset = offset;
        this.nonInteraction = nonInteraction;
        this.uiRegion = uiRegion;
    }
}
